// Helper function to generate unique row IDs
function generateRowId() {
  return `${Date.now()}-${Math.random()}-${Math.random()}`;
}

// Main export functions
export function mergeBulkDataWithDerivation(
  prev,
  incomingBulkData,
  allTables = {},
) {
  const incoming = Array.isArray(incomingBulkData) ? incomingBulkData : [];
  const existing = Array.isArray(prev) ? prev : [];

  // First, derive all necessary rows from the incoming bulk data
  const derivedIncoming = [];

  for (const row of incoming) {
    // Add the original row with a flag to identify it as QCad original
    derivedIncoming.push({
      ...row,
      _source: "qcad",
      _originalOrder: derivedIncoming.length,
    });

    // Derive additional rows if needed (reducer -> pipe+elbow, flange -> hardware)
    const derivedRows = buildDerivedRows(row, row.path || "", allTables);
    if (derivedRows.length > 0) {
      // Add derived rows with source flag and original order
      const derivedWithMeta = derivedRows.map((derivedRow, idx) => ({
        ...derivedRow,
        _source: "derived",
        _originalOrder: derivedIncoming.length + idx,
        _parentSource: row._source || "qcad",
      }));
      derivedIncoming.push(...derivedWithMeta);
    }
  }

  // Now merge using the existing merge logic
  const merged = mergeBulkData(existing, derivedIncoming);

  // Sort the merged array to put QCad rows first, then derived rows
  // and group fasteners together
  return sortBulkRows(merged);
}

// New function to sort bulk rows
function sortBulkRows(rows) {
  if (!Array.isArray(rows)) return rows;

  // Separate QCad rows from derived rows
  const qcadRows = rows.filter(
    (row) => row._source === "qcad" || !row._derived,
  );
  const derivedRows = rows.filter((row) => row._derived === true);

  // Sort QCad rows by their original order
  qcadRows.sort((a, b) => (a._originalOrder || 0) - (b._originalOrder || 0));

  // Group derived rows: first non-fastener derived rows, then grouped fasteners
  const nonFastenerDerived = [];
  const fastenerGroups = new Map(); // Group by parent flange or bolt set

  for (const row of derivedRows) {
    const component = (row.Component || "").toLowerCase();
    const isFastener = ["bolt", "nut", "washer"].includes(component);

    if (!isFastener) {
      nonFastenerDerived.push(row);
    } else {
      // Group fasteners by parent or bolt index
      const groupKey =
        row._parentId || row._boltIndex || row._derivedFrom || "fastener";
      if (!fastenerGroups.has(groupKey)) {
        fastenerGroups.set(groupKey, []);
      }
      fastenerGroups.get(groupKey).push(row);
    }
  }

  // Sort each fastener group to have Bolt first, then Nut, then Washer
  for (const [key, group] of fastenerGroups.entries()) {
    group.sort((a, b) => {
      const order = { bolt: 1, nut: 2, washer: 3 };
      const compA = (a.Component || "").toLowerCase();
      const compB = (b.Component || "").toLowerCase();
      return (order[compA] || 99) - (order[compB] || 99);
    });
  }

  // Flatten fastener groups back into array
  const groupedFasteners = [];
  for (const [key, group] of fastenerGroups.entries()) {
    groupedFasteners.push(...group);
  }

  // Combine: QCad rows first, then non-fastener derived, then grouped fasteners
  const sortedRows = [...qcadRows, ...nonFastenerDerived, ...groupedFasteners];

  // Remove temporary sorting properties
  return sortedRows.map((row) => {
    const { _originalOrder, _parentSource, ...cleanRow } = row;
    return cleanRow;
  });
}

export function mergeBulkData(prev, incomingBulkData) {
  const incoming = Array.isArray(incomingBulkData) ? incomingBulkData : [];
  const existing = Array.isArray(prev) ? prev : [];
  const merged = [...existing];

  for (const row of incoming) {
    const matchIndex = merged.findIndex(
      (r) =>
        String(r?.["Line Number"] || "").trim() ===
        String(row?.["Line Number"] || "").trim() &&
        String(r?.Spec || "").trim() === String(row?.Spec || "").trim() &&
        String(r?.Component || "").trim() ===
        String(row?.Component || "").trim() &&
        String(r?.Size || "").trim() === String(row?.Size || "").trim() &&
        String(r?.["Schedule/Rating"] || "").trim() ===
        String(row?.["Schedule/Rating"] || "").trim() &&
        String(r?.Ends || "").trim() === String(row?.Ends || "").trim() &&
        String(r?.Material || "").trim() ===
        String(row?.Material || "").trim() &&
        String(r?.["Material Description"] || "").trim() ===
        String(row?.["Material Description"] || "").trim(),
    );

    if (matchIndex !== -1) {
      // Accumulate Qty
      const existingQty = Number(merged[matchIndex].Qty) || 1;
      const incomingQty = Number(row.Qty) || 1;

      merged[matchIndex] = {
        ...merged[matchIndex],
        Qty: existingQty + incomingQty,
      };
    } else {
      merged.push({
        ...row,
        Qty: row.Qty || 1,
        _rowId: row._rowId || generateRowId(),
      });
    }
  }

  return merged;
}

// Helper functions for text normalization
function normalizeText(value) {
  return String(value ?? "")
    .replace(/[″"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeFlangeRating(value) {
  const raw = normalizeText(value);
  if (!raw) return "";

  if (/^class\s+/i.test(raw)) {
    return raw.replace(/^class\s+/i, "Class ");
  }

  if (/^\d+$/.test(raw)) {
    return `Class ${raw}`;
  }

  return raw;
}

function normalizeComponentName(component) {
  return String(component || "")
    .trim()
    .toLowerCase();
}

// Value extraction helper
function getValueByExactOrContains(row, exactKeys = [], containsKeys = []) {
  if (!row || typeof row !== "object") return "";

  for (const key of exactKeys) {
    if (
      row[key] !== undefined &&
      row[key] !== null &&
      String(row[key]).trim() !== ""
    ) {
      return row[key];
    }
  }

  const keys = Object.keys(row);
  for (const search of containsKeys) {
    const foundKey = keys.find((k) =>
      String(k).toLowerCase().includes(String(search).toLowerCase()),
    );
    if (
      foundKey &&
      row[foundKey] !== undefined &&
      row[foundKey] !== null &&
      String(row[foundKey]).trim() !== ""
    ) {
      return row[foundKey];
    }
  }

  return "";
}

// Component type checkers
function isFlangeComponent(component) {
  const value = normalizeComponentName(component);
  return value.includes("flange");
}

// Flange bolt helpers
function findFlangeBoltRow(flangeRow, allTables) {
  const table = Array.isArray(allTables?.["Flange Bolt"])
    ? allTables["Flange Bolt"]
    : [];

  const targetNps = normalizeText(flangeRow.Size);
  const targetRating = normalizeFlangeRating(flangeRow["Schedule/Rating"]);

  const match = table.find((dbRow) => {
    const dbNps = normalizeText(
      getValueByExactOrContains(dbRow, ["NPS"], ["nps"]),
    );

    const dbRating = normalizeFlangeRating(
      getValueByExactOrContains(
        dbRow,
        ["Flange Rating", "FlangeRating"],
        ["flange rating", "rating"],
      ),
    );

    return dbNps === targetNps && dbRating === targetRating;
  });

  // console.log("[Flange Bolt Debug] lookup:", {
  //   targetNps,
  //   targetRating,
  //   found: match || null,
  // });

  return match || null;
}

function getBoltCount(flangeBoltRow) {
  if (!flangeBoltRow) return 0;

  const raw = getValueByExactOrContains(
    flangeBoltRow,
    ["Number of Bolts", "Number Of Bolts", "Number of Bollts"],
    ["number of bolt", "number of bollt", "no of bolt"],
  );

  const count = Number(raw);
  return Number.isFinite(count) ? count : 0;
}

function getBoltSize(flangeBoltRow, fallback = "") {
  if (!flangeBoltRow) return fallback;

  return normalizeText(
    getValueByExactOrContains(
      flangeBoltRow,
      ["Bolt Diameter", "Bolt Diametere", "Bolt Dia"],
      ["bolt diameter", "bolt dia"],
    ) || fallback,
  );
}

function getBoltLength(flangeBoltRow) {
  if (!flangeBoltRow) return "";

  return normalizeText(
    getValueByExactOrContains(
      flangeBoltRow,
      ["Bolt Length", "Bolt Length (mm)", "Bolt Length (in)"],
      ["bolt length"],
    ),
  );
}

// Main derivation function
function buildDerivedRows(bulkNeededData, pnidDrawingPath, allTables = {}) {
  // console.log("[Derive Debug] incoming bulk row:", bulkNeededData);

  const component = bulkNeededData.Component || "";
  const derivedRows = [];
  const parentLineNumber =
    bulkNeededData["Line Number"] || bulkNeededData.sourceLineNumber || "";

  // Flange -> Gasket + Bolt + Nut + Washer
  if (isFlangeComponent(component)) {
    const flangeSize = normalizeText(bulkNeededData.Size);

    if (flangeSize) {
      derivedRows.push({
        Spec: bulkNeededData.Spec || "",
        Component: "Gasket",
        Size: flangeSize,
        "Schedule/Rating": bulkNeededData["Schedule/Rating"] || "",
        Ends: "",
        Material: "",
        Category: "Bulk",
        path: pnidDrawingPath,
        "Line Number": parentLineNumber,
        sourceLineNumber: parentLineNumber,
        _derived: true,
        _derivedFrom: "flange",
        _parentComponent: component,
      });
    }

    const flangeBoltRow = findFlangeBoltRow(bulkNeededData, allTables);
    const boltCount = getBoltCount(flangeBoltRow);
    const boltSize = getBoltSize(flangeBoltRow, flangeSize);
    const boltLength = getBoltLength(flangeBoltRow);

    // console.log("[Flange Bolt Debug] derived hardware:", {
    //   flange: bulkNeededData,
    //   boltCount,
    //   boltSize,
    //   boltLength,
    // });

    if (boltCount > 0 && boltSize) {
      for (let i = 0; i < boltCount; i++) {
        derivedRows.push({
          Spec: bulkNeededData.Spec || "",
          Component: "Bolt",
          Size: boltSize,
          "Schedule/Rating": "",
          Ends: "",
          Material: "",
          "Material Description": "", // boltLength ? `Bolt Length ${boltLength}` : "",
          Category: "Bulk",
          path: pnidDrawingPath,
          "Line Number": parentLineNumber,
          sourceLineNumber: parentLineNumber,
          _derived: true,
          _derivedFrom: "flange",
          _boltIndex: i,
          _flangeParent: {
            Spec: bulkNeededData.Spec,
            Size: flangeSize,
            Rating: bulkNeededData["Schedule/Rating"],
          },
        });

        derivedRows.push({
          Spec: bulkNeededData.Spec || "",
          Component: "Nut",
          Size: boltSize,
          "Schedule/Rating": "",
          Ends: "",
          Material: "",
          Category: "Bulk",
          path: pnidDrawingPath,
          "Line Number": parentLineNumber,
          sourceLineNumber: parentLineNumber,
          _derived: true,
          _derivedFrom: "flange",
          _boltIndex: i,
        });

        derivedRows.push({
          Spec: bulkNeededData.Spec || "",
          Component: "Washer",
          Size: boltSize,
          "Schedule/Rating": "",
          Ends: "",
          Material: "",
          Category: "Bulk",
          path: pnidDrawingPath,
          "Line Number": parentLineNumber,
          sourceLineNumber: parentLineNumber,
          _derived: true,
          _derivedFrom: "flange",
          _boltIndex: i,
        });
      }
    } else {
      // console.warn("[Flange Bolt Debug] No bolt data found for flange:", {
      //   flange: bulkNeededData,
      //   flangeBoltRow,
      //   boltCount,
      //   boltSize,
      // });
    }
  }

  // console.log("[Derive Debug] final derived rows:", derivedRows);
  return derivedRows;
}

// Optional: Export individual helpers if needed elsewhere
export {
  normalizeText,
  normalizeFlangeRating,
  isFlangeComponent,
  generateRowId,
};
